import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useGifStore } from './gif-store';
import { GIF_TEMPLATES, GifTemplateId } from './types';
import { LayoutTemplate } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState } from 'react';

/**
 * F10: GIF Templates - one-click preset selector
 */
export function GifTemplateSelector() {
  const applyTemplate = useGifStore((s) => s.applyTemplate);
  const settings = useGifStore((s) => s.settings);
  const [selected, setSelected] = useState<GifTemplateId>('custom');

  const handleSelect = (id: GifTemplateId) => {
    setSelected(id);
    applyTemplate(id);
  };

  // Compute estimated file size based on quality + width (very rough heuristic)
  const estimateSize = (width: number, quality: 'low' | 'medium' | 'high' | 'custom') => {
    const baseKb = (width / 100) * (quality === 'low' ? 4 : quality === 'medium' ? 8 : 16);
    return Math.round(baseKb);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <LayoutTemplate className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">Templates</CardTitle>
        </div>
        <CardDescription>One-click presets for common use cases</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {GIF_TEMPLATES.map((template) => {
            const isSelected = selected === template.id;
            const width = template.settings.maxWidth ?? settings.maxWidth;
            const quality = (template.settings.quality ?? settings.quality) as 'low' | 'medium' | 'high' | 'custom';
            return (
              <Button
                key={template.id}
                type="button"
                variant="outline"
                onClick={() => handleSelect(template.id)}
                className={cn(
                  'flex h-auto flex-col items-start gap-1 p-3 text-left',
                  isSelected && 'border-primary bg-primary/5',
                )}
              >
                <span className="flex w-full items-center gap-2">
                  <span aria-hidden="true" className="text-base">{template.icon}</span>
                  <span className="font-medium text-sm">{template.name}</span>
                </span>
                <span className="text-xs text-muted-foreground line-clamp-2">{template.description}</span>
                {template.id !== 'custom' && (
                  <span className="text-[10px] text-muted-foreground/70 mt-1">
                    {template.settings.frameRate}fps · {width}px · ~{estimateSize(width, quality)}KB/frame
                  </span>
                )}
              </Button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default GifTemplateSelector;
